import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  LoadingSkeleton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  HairlineCard,
  Icons,
  iconSize,
  MenuItem,
  Stack,
  StatusChip,
  TextField,
  Typography,
  useSnackbar,
} from '@munifocus/ui';
import {
  DECLINE_PUBLIC_REASONS,
  type DeclinePublicReason,
  type TriageQueueItem,
} from '@munifocus/contracts-gov';
import { AppShell } from '../components/AppShell';
import { PageIntro } from '../components/PageIntro';
import { CitizenReportDetail, foldTargets } from '../components/CitizenReportDetail';
import { useOptionalScope } from '../providers/ScopeProvider';
import { apiErrorMessage } from '../lib/mutation-toast';
import { useDeclineReport, useMergeReport, useTriageQueue } from '../lib/triage-api';
import { useSubmitValidation } from '../lib/useSubmitValidation';
import { formatTriageDate } from '../lib/triage-dates';

/**
 * Reports from the public, waiting on a decision.
 *
 * The screen exists because the public form does not open files. Somebody
 * outside the municipality can describe a property; an officer here decides
 * whether that becomes an enforcement file, and the deciding is the point —
 * every report gets an answer, including the ones that get "no".
 *
 * Accepting is deliberately not a button. It hands the officer the intake form
 * with the report's address and description carried over, and a file is created
 * by the ordinary path with every check that applies to any other file. The
 * report flips to accepted when the file actually exists, so the two cannot
 * disagree.
 */
export function Triage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const queue = useTriageQueue();
  const decline = useDeclineReport();
  const fold = useMergeReport();
  const scope = useOptionalScope();
  // The server is the gate; this only hides a button the caller could not use.
  const canFold = scope ? scope.can('report:triage') : true;

  const [declining, setDeclining] = useState<TriageQueueItem | null>(null);
  const [reason, setReason] = useState('');
  const [publicReason, setPublicReason] = useState<DeclinePublicReason>('other');
  const [folding, setFolding] = useState<TriageQueueItem | null>(null);
  const [foldInto, setFoldInto] = useState('');
  const [foldNote, setFoldNote] = useState('');

  const items = queue.data ?? [];
  const open = useMemo(() => items.filter((i) => i.status === 'untriaged'), [items]);
  const settled = useMemo(() => items.filter((i) => i.status !== 'untriaged'), [items]);

  const formatDate = (iso: string) => formatTriageDate(iso, i18n.language);

  /**
   * Carry the report into intake rather than creating anything here.
   *
   * The officer reviews and completes the form as they would for any file; the
   * complaint id travels with it so the report is marked accepted at the moment
   * the file exists.
   */
  const accept = (item: TriageQueueItem) => {
    navigate(`/files/new?complaintId=${item.id}`);
  };

  const v = useSubmitValidation({
    reason: reason.trim() ? null : t('gov.triage.declineReasonRequired'),
  });
  const closeDecline = () => {
    setDeclining(null);
    v.reset();
  };

  const confirmDecline = () => {
    if (!declining || reason.trim() === '') return;
    decline.mutate(
      {
        id: declining.id,
        reason: reason.trim(),
        publicReason: declining.citizenReport ? publicReason : undefined,
      },
      {
        onSuccess: () => {
          snackbar.success(t('gov.triage.declined'));
          closeDecline();
          setReason('');
        },
        onError: (error) => snackbar.error(apiErrorMessage(error)),
      },
    );
  };

  const openFold = (item: TriageQueueItem) => {
    setFolding(item);
    setFoldInto(foldTargets(item)[0]?.id ?? '');
    setFoldNote('');
  };

  const confirmFold = () => {
    if (!folding || !foldInto) return;
    fold.mutate(
      { id: folding.id, fileId: foldInto, note: foldNote.trim() || undefined },
      {
        onSuccess: () => {
          snackbar.success(t('gov.triage.merged'));
          setFolding(null);
        },
        onError: (error) => snackbar.error(apiErrorMessage(error)),
      },
    );
  };

  const card = (item: TriageQueueItem) => (
    <HairlineCard key={item.id} sx={{ p: { xs: 2, sm: 2.5 } }}>
      <Stack spacing={1.5}>
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap' }}
        >
          <Box>
            <Typography variant="subtitle1">{item.subjectAddress}</Typography>
            <Typography variant="caption">
              {item.reference} · {formatDate(item.createdAt)}
            </Typography>
          </Box>
          <StatusChip
            label={t(`gov.triage.status.${item.status}`)}
            status={
              item.status === 'accepted' || item.status === 'merged'
                ? 'success'
                : item.status === 'declined'
                  ? 'neutral'
                  : 'pending'
            }
          />
        </Stack>

        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {item.description}
        </Typography>

        {item.citizenReport && <CitizenReportDetail item={item} />}

        <Typography variant="caption">
          {/* Three states, not two. Choosing anonymity and simply leaving the
              name blank both end up without a name, but they are different
              things and an officer reading the queue should not be told the
              reporter asked for anonymity when they did not. */}
          {item.anonymous
            ? t('gov.triage.anonymous')
            : item.complainantEmail || item.complainantPhone
              ? t('gov.triage.from', {
                  name: item.complainantName || t('gov.triage.noName'),
                  contact: item.complainantEmail || item.complainantPhone,
                })
              : t('gov.triage.noContact')}
        </Typography>

        {/* Shown, never acted on: a second report about a property already
            under enforcement is corroboration, and the officer decides whether
            it is a duplicate. */}
        {item.openFiles.length > 0 && (
          <Alert severity="info" icon={<Icons.Info size={iconSize.sm} />}>
            {t('gov.triage.alreadyOpen', {
              files: item.openFiles.map((f) => f.reference).join(', '),
            })}
          </Alert>
        )}

        {item.status === 'untriaged' && (
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
            <Button variant="contained" onClick={() => accept(item)}>
              {t('gov.triage.accept')}
            </Button>
            {canFold && foldTargets(item).length > 0 && (
              <Button
                variant="outlined"
                startIcon={<Icons.Link2 size={iconSize.sm} />}
                onClick={() => openFold(item)}
              >
                {t('gov.triage.merge')}
              </Button>
            )}
            <Button variant="outlined" onClick={() => setDeclining(item)}>
              {t('gov.triage.decline')}
            </Button>
          </Stack>
        )}

        {item.status === 'merged' && item.mergedIntoFileId && (
          <Button
            variant="text"
            sx={{ alignSelf: 'flex-start' }}
            onClick={() => navigate(`/files/${item.mergedIntoFileId}`)}
          >
            {t('gov.triage.mergedInto', {
              reference: item.mergedIntoFileReference ?? '—',
              name: item.triagedByName || '—',
            })}
          </Button>
        )}

        {item.status === 'accepted' && item.fileReference && (
          <Button
            variant="text"
            sx={{ alignSelf: 'flex-start' }}
            onClick={() => navigate(`/files/${item.fileId}`)}
          >
            {t('gov.triage.openFile', { reference: item.fileReference })}
          </Button>
        )}

        {item.status === 'declined' && (
          <Typography variant="caption">
            {t('gov.triage.declinedBy', {
              name: item.triagedByName || '—',
              reason: item.declinedReason || '—',
            })}
          </Typography>
        )}
      </Stack>
    </HairlineCard>
  );

  return (
    <AppShell>
      <PageIntro>{t('gov.triage.intro')}</PageIntro>
      <Stack spacing={2}>
        {queue.isLoading && <LoadingSkeleton variant="cards" label={t('common.labels.loading')} />}
        {queue.isError && <Alert severity="error">{t('gov.triage.loadError')}</Alert>}

        {!queue.isLoading && open.length === 0 && (
          <Alert severity="success">{t('gov.triage.empty')}</Alert>
        )}

        {open.map(card)}

        {settled.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              {t('gov.triage.settled')}
            </Typography>
            {settled.map(card)}
          </>
        )}
      </Stack>

      <Dialog open={declining !== null} onClose={closeDecline} fullWidth maxWidth="sm">
        <DialogTitle>{t('gov.triage.declineTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {/* The reason is required, not optional: the person who made this
                report may ask months later, and "declined" is not an answer. */}
            <Typography variant="body2">{t('gov.triage.declineBody')}</Typography>
            <TextField
              required
              multiline
              minRows={3}
              label={t('gov.triage.declineReason')}
              {...v.field('reason')}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
            {/* A citizen report's reporter is told something; never the
                reason above, which is written for colleagues. */}
            {declining?.citizenReport && (
              <TextField
                select
                label={t('gov.triage.publicReason')}
                helperText={t('gov.triage.publicReasonHelp')}
                value={publicReason}
                onChange={(event) => setPublicReason(event.target.value as DeclinePublicReason)}
              >
                {DECLINE_PUBLIC_REASONS.map((key) => (
                  <MenuItem key={key} value={key}>
                    {t(`common.citizenReports.declineReason.${key}`)}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDecline}>{t('common.actions.cancel')}</Button>
          <Button
            variant="contained"
            disabled={decline.isPending}
            onClick={v.guard(confirmDecline)}
          >
            {t('gov.triage.confirmDecline')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={folding !== null} onClose={() => setFolding(null)} fullWidth maxWidth="sm">
        <DialogTitle>{t('gov.triage.mergeTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2">{t('gov.triage.mergeBody')}</Typography>
            <TextField
              select
              required
              label={t('gov.triage.mergeInto')}
              value={foldInto}
              onChange={(event) => setFoldInto(event.target.value)}
            >
              {(folding ? foldTargets(folding) : []).map((target) => (
                <MenuItem key={target.id} value={target.id}>
                  {target.distanceM !== null
                    ? t('gov.triage.mergeOptionNear', {
                        reference: target.reference,
                        distance: target.distanceM,
                      })
                    : t('gov.triage.mergeOptionAddress', { reference: target.reference })}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              multiline
              minRows={2}
              label={t('gov.triage.mergeNote')}
              helperText={t('gov.triage.mergeNoteHelp')}
              value={foldNote}
              onChange={(event) => setFoldNote(event.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFolding(null)}>{t('common.actions.cancel')}</Button>
          <Button variant="contained" disabled={fold.isPending || !foldInto} onClick={confirmFold}>
            {t('gov.triage.confirmMerge')}
          </Button>
        </DialogActions>
      </Dialog>
    </AppShell>
  );
}
